// src/services/useWorkflowsStore.js
import { create } from "zustand";
import { api } from "./api";

export const useWorkflowsStore = create((set, get) => ({
  // State
  workflows: [],
  currentWorkflow: null,
  nodes: [],
  edges: [],
  executions: [],
  loading: false,
  error: null,

  // Actions
  fetchWorkflows: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get("/api/workflows");
      set({ workflows: response.data, loading: false });
      return response.data;
    } catch (error) {
      console.error("Error fetching workflows:", error);
      set({
        error: error.response?.data?.error || "Failed to fetch workflows",
        loading: false,
      });
      throw error;
    }
  },

  createWorkflow: async (workflowData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post("/api/workflows", workflowData);
      const newWorkflow = response.data;

      set((state) => ({
        workflows: [...state.workflows, newWorkflow],
        loading: false,
      }));

      return newWorkflow;
    } catch (error) {
      console.error("Error creating workflow:", error);
      set({
        error: error.response?.data?.error || "Failed to create workflow",
        loading: false,
      });
      throw error;
    }
  },

  fetchWorkflowById: async (workflowId) => {
    set({ loading: true, error: null });
    try {
      const response = await api.get(`/api/workflows/${workflowId}`);
      const workflow = response.data;

      set({
        currentWorkflow: workflow,
        nodes: workflow.nodes || [],
        edges: workflow.edges || [],
        loading: false,
      });

      return workflow;
    } catch (error) {
      console.error("Error fetching workflow:", error);
      set({
        error: error.response?.data?.error || "Failed to fetch workflow",
        loading: false,
      });
      throw error;
    }
  },

  updateWorkflow: async (workflowId, workflowData) => {
    set({ loading: true, error: null });
    try {
      const response = await api.put(`/api/workflows/${workflowId}`, workflowData);
      const updatedWorkflow = response.data;

      set((state) => ({
        workflows: state.workflows.map((w) =>
          w.id === workflowId ? updatedWorkflow : w
        ),
        currentWorkflow: updatedWorkflow,
        loading: false,
      }));

      return updatedWorkflow;
    } catch (error) {
      console.error("Error updating workflow:", error);
      set({
        error: error.response?.data?.error || "Failed to update workflow",
        loading: false,
      });
      throw error;
    }
  },

  deleteWorkflow: async (workflowId) => {
    set({ loading: true, error: null });
    try {
      await api.delete(`/api/workflows/${workflowId}`);

      set((state) => ({
        workflows: state.workflows.filter((w) => w.id !== workflowId),
        loading: false,
      }));
    } catch (error) {
      console.error("Error deleting workflow:", error);
      set({
        error: error.response?.data?.error || "Failed to delete workflow",
        loading: false,
      });
      throw error;
    }
  },

  executeWorkflow: async (workflowId, triggerData = null) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post(`/api/workflows/${workflowId}/execute`, {
        trigger_data: triggerData,
      });

      set({ loading: false });
      return response.data;
    } catch (error) {
      console.error("Error executing workflow:", error);
      set({
        error: error.response?.data?.error || "Failed to execute workflow",
        loading: false,
      });
      throw error;
    }
  },

  activateWorkflow: async (workflowId) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post(`/api/workflows/${workflowId}/activate`);
      const updatedWorkflow = response.data;

      set((state) => ({
        workflows: state.workflows.map((w) =>
          w.id === workflowId ? updatedWorkflow : w
        ),
        currentWorkflow: updatedWorkflow,
        loading: false,
      }));

      return updatedWorkflow;
    } catch (error) {
      console.error("Error activating workflow:", error);
      set({
        error: error.response?.data?.error || "Failed to activate workflow",
        loading: false,
      });
      throw error;
    }
  },

  deactivateWorkflow: async (workflowId) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post(`/api/workflows/${workflowId}/deactivate`);
      const updatedWorkflow = response.data;

      set((state) => ({
        workflows: state.workflows.map((w) =>
          w.id === workflowId ? updatedWorkflow : w
        ),
        currentWorkflow: updatedWorkflow,
        loading: false,
      }));

      return updatedWorkflow;
    } catch (error) {
      console.error("Error deactivating workflow:", error);
      set({
        error: error.response?.data?.error || "Failed to deactivate workflow",
        loading: false,
      });
      throw error;
    }
  },

  fetchExecutions: async (workflowId) => {
    set({ loading: true, error: null });
    try {
      const response = await api.get(`/api/workflows/${workflowId}/executions`);
      set({ executions: response.data, loading: false });
      return response.data;
    } catch (error) {
      console.error("Error fetching executions:", error);
      set({
        error: error.response?.data?.error || "Failed to fetch executions",
        loading: false,
      });
      throw error;
    }
  },

  // Local state management for canvas
  setNodes: (nodes) => {
    set({ nodes });
  },

  setEdges: (edges) => {
    set({ edges });
  },

  clearError: () => {
    set({ error: null });
  },

  resetWorkflow: () => {
    set({
      currentWorkflow: null,
      nodes: [],
      edges: [],
      executions: [],
    });
  },
}));
